const cities = [
  { name: "Baton Rouge", zips: "70801, 70802, 70803, 70805, 70806, 70808, 70809, 70810, 70814, 70815, 70816, 70817, 70818, 70819, 70820" },
  { name: "Port Allen", zips: "70767" },
  { name: "Zachary", zips: "70791" },
  { name: "Baker", zips: "70714" },
  { name: "Central", zips: "70818, 70837" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your home, LSU dorm, or state office in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle every detail from there.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and prevent delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function BatonRougeLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Baton Rouge &amp; Louisiana's Capital Region
          </h2>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Baton Rouge, Louisiana's state capital, is a city shaped by government, education, and industry. Louisiana State University (LSU) sends hundreds of students abroad each year through study-abroad programs, international research partnerships, and athletic competitions. State government employees regularly travel for trade missions, legislative conferences, and international partnerships. The petrochemical industry along the Mississippi River sends engineers and executives to Europe, Asia, and the Middle East for plant inspections, vendor negotiations, and technical training.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              The Baton Rouge Metropolitan Airport (BTR) offers connections to major international hubs, and many residents also drive to New Orleans (MSY) for direct international flights. The city's proximity to both the Gulf Coast and major Texas airports creates a travel pattern where residents often book flights with relatively short notice — especially for petrochemical assignments and LSU program deadlines.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              The study-abroad timeline is particularly compressed. LSU application deadlines often fall in October and February, but many students do not think about their passport until after acceptance. Standard post office processing — currently 1–3 months — frequently exceeds program deadlines, making expedited service essential. Petrochemical engineers often receive overseas assignment notice with 2–4 weeks to deploy, and state employees may receive trade mission invitations on similarly short timelines.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              Fast Passport Center was built for exactly these scenarios. We provide the expedited, professional passport service that Baton Rouge's diverse traveler base demands: dedicated agents who understand LSU program deadlines, document review that catches errors before they cost you a petrochemical assignment, and processing as fast as 24 hours for true emergencies. No driving to Houston. No waiting for an appointment that may never open. Just reliable, predictable service from a registered U.S. Department of State courier.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Baton Rouge, LA</p>
                  <p className="text-white/60 text-xs mt-0.5">Serving Baton Rouge, Port Allen, Zachary & Capital Region</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Remote service — no local office visit needed
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout East Baton Rouge and surrounding parishes</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or office in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location, or <strong className="text-white">schedule a drop-off</strong> at our processing center. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono hidden sm:inline">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in the Capital Region — start online, ship overnight, and we handle the rest. No need to drive to Houston or New Orleans for regional agency appointments.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "New Orleans", href: "/cities/new-orleans" },
                    { name: "Shreveport", href: "/cities/shreveport" },
                    { name: "Lafayette", href: "/states/louisiana" },
                    { name: "Lake Charles", href: "/states/louisiana" },
                    { name: "Hammond", href: "/states/louisiana" },
                    { name: "Alexandria", href: "/states/louisiana" },
                  ].map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                    >
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Baton Rouge Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}